import { type Dtos, pluralize, type WipSchedule } from "@inrev/common";
import { Option } from "@inrev/core";
import { type FC, type ReactNode, useId, useMemo } from "react";
import { formatEmpty } from "../../../utils/string";
import { cn } from "../../lib/utils";
import { SideDrawerTrigger, useSideDrawer } from "../../patterns/side-drawer";
import { Text } from "../../primitives";
import type { ColumnDef, DataGridColumnEntry, DataGridRow } from "../../ui/DataGrid";
import { DataGrid } from "../../ui/DataGrid";
import { PortaledTooltip } from "../../ui/Tooltip";
import {
	buildWipAnalysisGridViewModel,
	indexWipAnalysisLineItems,
	WIP_ANALYSIS_PORTFOLIO_TOTAL_LABEL,
	type WipAnalysisGridCell,
	type WipAnalysisGridMetricColumn,
	type WipAnalysisGridPeriod,
	type WipAnalysisGridProjectRow,
} from "./buildWipAnalysisGridViewModel";
import {
	WIP_ANALYSIS_PERIOD_SELECTOR_ID,
	WipAnalysisPeriodSelectorDrawer,
} from "./WipAnalysisPeriodSelectorDrawer";
import { useWipAnalysisPeriodSelector } from "./WipAnalysisPeriodSelectorProvider";
import { WIP_COLUMN_SELECTOR_ID, WipColumnSelectorDrawer } from "./WipColumnSelectorDrawer";
import { useWipColumnSelector } from "./WipColumnSelectorProvider";
import type { WipAnalysisGridMetricId } from "./wipMetric";
import { compareExtractions } from "./wipPeriod";

const WIP_IDENTITY_LABEL_MAX_PX = 420;

const WIP_IDENTITY_LABEL_WIDTH = `minmax(360px, ${WIP_IDENTITY_LABEL_MAX_PX}px)`;

const WIP_ANALYSIS_GRID_SCROLL_PADDING = { left: WIP_IDENTITY_LABEL_MAX_PX, right: 128 };

const WIP_PERIOD_MIN_WIDTH_PX = 220;

const IDENTITY_GRID_CLASS =
	"grid w-full min-w-0 h-full items-center gap-2 [grid-template-columns:5rem_minmax(0,1fr)_4.5rem]";

const METRIC_CELL_CONTAINER_CLASS = "flex items-center justify-end gap-1 pr-2.5 h-full w-full";

const STATUS_CELL_CLASS = "flex h-full w-full min-w-0 items-center justify-center";

type WipAnalysisGridRowData = {
	cells: Record<string, WipAnalysisGridCell>;
};

export type WipAnalysisGridProps = {
	extractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>;
	lineItems: ReadonlyArray<Dtos.Wip.LineItem.Get.Response>;
	projects: ReadonlyArray<Dtos.Wip.Project.Get.Response>;
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>;
};

const wipJobStatusLabel = (status: WipSchedule.WipJobStatus): string =>
	status === "open" ? "Open" : "Closed";

const WipJobStatusBadge: FC<{ status: WipSchedule.WipJobStatus }> = ({ status }) => {
	const isOpen = status === "open";
	return (
		<span
			data-testid={`wip-job-status-${status}`}
			className="inline-flex items-center gap-1 min-w-0"
		>
			<span
				aria-hidden
				className={cn("size-1 shrink-0 rounded-full", isOpen ? "bg-confirm" : "bg-bc-stronger")}
			/>
			<Text type="body-strong" color={isOpen ? "confirm" : "tertiary"} truncate>
				{wipJobStatusLabel(status)}
			</Text>
		</span>
	);
};

const WipJobStatusCell: FC<{ status: WipSchedule.WipJobStatus | null }> = ({ status }) => (
	<div data-testid="wip-job-status-cell" className={STATUS_CELL_CLASS}>
		{Option.isSome(status) ? (
			<WipJobStatusBadge status={status} />
		) : (
			<Text type="body" color="tertiary">
				{formatEmpty(null)}
			</Text>
		)}
	</div>
);

const WipIdentityRow: FC<{ children: ReactNode; testId?: string }> = ({ children, testId }) => (
	<div className={IDENTITY_GRID_CLASS} data-testid={testId}>
		{children}
	</div>
);

const WipIdentityHeader: FC = () => (
	<div data-testid="wip-identity-header" className={IDENTITY_GRID_CLASS}>
		<Text truncate>Project #</Text>
		<Text truncate>Project Name</Text>
		<div className={STATUS_CELL_CLASS}>
			<Text truncate>Status</Text>
		</div>
	</div>
);

const WipHiddenProjectsIndicator: FC<{ count: number }> = ({ count }) => {
	const tooltipId = useId();
	return (
		<span
			tabIndex={0}
			data-testid="wip-analysis-hidden-projects"
			data-tooltip-id={tooltipId}
			data-tooltip-content={`${pluralize(count, "line item is", "line items are")} missing a project assignment. Counted in the Portfolio Total, but not shown as project rows.`}
			className="flex cursor-help"
		>
			<Text type="caption" color="warning" className="text-center">
				{pluralize(count, "hidden project", "hidden projects")}
			</Text>
			<PortaledTooltip id={tooltipId} />
		</span>
	);
};

const WipAnalysisPeriodHeader: FC<{ period: WipAnalysisGridPeriod }> = ({ period }) => (
	<div
		data-testid="wip-analysis-period-header"
		className="flex items-center justify-center px-2.5 py-2.5 h-full"
	>
		<div className="flex flex-col items-center justify-center gap-2.5">
			<Text type="body-strong" color="primary" className="text-center">
				{period.dateLabel}
			</Text>
			<Text type="caption" color="secondary" className="text-center">
				{period.periodTypeLabel}
			</Text>
			{period.unassignedLineItemCount > 0 && (
				<WipHiddenProjectsIndicator count={period.unassignedLineItemCount} />
			)}
		</div>
	</div>
);

const WipProjectIdentityLabel: FC<{ row: WipAnalysisGridProjectRow }> = ({ row }) => (
	<WipIdentityRow testId="wip-analysis-project-row">
		<Text type="body" color="tertiary" truncate data-testid="wip-identity-project-number">
			{formatEmpty(row.projectNumber)}
		</Text>
		<Text type="body-strong" color="primary" truncate data-testid="wip-identity-project-name">
			{formatEmpty(row.projectName)}
		</Text>
		<WipJobStatusCell status={row.jobStatus} />
	</WipIdentityRow>
);

const WipPortfolioTotalIdentityLabel: FC = () => (
	<WipIdentityRow>
		<Text type="body" color="tertiary" />
		<Text type="body-strong" color="primary" truncate data-testid="wip-portfolio-total-label">
			{WIP_ANALYSIS_PORTFOLIO_TOTAL_LABEL}
		</Text>
		<span />
	</WipIdentityRow>
);

const emptyPeriodColumn = (periodId: string): ColumnDef<WipAnalysisGridRowData> => ({
	id: `${periodId}::empty`,
	label: "",
	width: `${WIP_PERIOD_MIN_WIDTH_PX}px`,
	navigable: false,
	renderCell: () => null,
});

const buildMetricColumn = (
	column: WipAnalysisGridMetricColumn,
	isColumnVisible: (metricId: WipAnalysisGridMetricId) => boolean,
	visibleCount: number,
): ColumnDef<WipAnalysisGridRowData> => ({
	id: column.id,
	label: column.label,
	width: `max(${column.width}, ${WIP_PERIOD_MIN_WIDTH_PX / Math.max(visibleCount, 1)}px)`,
	navigable: true,
	visible: isColumnVisible(column.metricId),
	renderCell: (row) => {
		const cell = row.data?.cells[column.id];
		return (
			<div
				className={METRIC_CELL_CONTAINER_CLASS}
				data-testid="wip-analysis-metric-cell"
				data-column-id={column.metricId}
			>
				<Text type="body" color="primary" className="tabular-nums">
					{formatEmpty(cell?.display)}
				</Text>
			</div>
		);
	},
});

const projectRowId = (projectId: string): string => `wip-project-${projectId}`;

const TOTAL_ROW_ID = "wip-portfolio-total";

export const WipAnalysisGrid: FC<WipAnalysisGridProps> = ({
	extractions,
	lineItems,
	projects,
	statements,
}) => {
	const { isWipColumnVisible, visibleCount } = useWipColumnSelector();
	const { selectedExtractionIds, periodOrder, selectedCount, hasPeriods } =
		useWipAnalysisPeriodSelector();
	const periodDrawer = useSideDrawer({
		testId: WIP_ANALYSIS_PERIOD_SELECTOR_ID,
		trackingId: WIP_ANALYSIS_PERIOD_SELECTOR_ID,
	});
	const columnDrawer = useSideDrawer({
		testId: WIP_COLUMN_SELECTOR_ID,
		trackingId: WIP_COLUMN_SELECTOR_ID,
	});

	const selectedExtractions = useMemo(
		() =>
			extractions
				.filter((extraction) => selectedExtractionIds.has(extraction.id))
				.sort(compareExtractions(periodOrder)),
		[extractions, selectedExtractionIds, periodOrder],
	);

	const labelColumnHeader = useMemo(
		() => (
			<div className="flex items-center gap-2">
				<SideDrawerTrigger
					{...periodDrawer.triggerProps}
					count={selectedCount}
					label="Periods"
					iconType="calendar"
					showLabel
				/>
				<SideDrawerTrigger
					{...columnDrawer.triggerProps}
					count={visibleCount}
					label="Columns"
					iconType="view-columns"
					showLabel
				/>
			</div>
		),
		[periodDrawer.triggerProps, columnDrawer.triggerProps, selectedCount, visibleCount],
	);

	const gridOverlay = useMemo(
		() => (
			<>
				<WipAnalysisPeriodSelectorDrawer {...periodDrawer.drawerProps} />
				<WipColumnSelectorDrawer {...columnDrawer.drawerProps} />
			</>
		),
		[periodDrawer.drawerProps, columnDrawer.drawerProps],
	);

	const lineItemIndex = useMemo(() => indexWipAnalysisLineItems(lineItems), [lineItems]);

	const viewModel = useMemo(
		() => buildWipAnalysisGridViewModel(selectedExtractions, lineItemIndex, projects, statements),
		[selectedExtractions, lineItemIndex, projects, statements],
	);

	const columns = useMemo<DataGridColumnEntry<WipAnalysisGridRowData>[]>(
		() =>
			viewModel.periodGroups.map((group) => ({
				id: group.id,
				label: group.period.dateLabel,
				subLabel: Option.toUndefined(group.period.statementDate),
				renderGroupHeader: () => <WipAnalysisPeriodHeader period={group.period} />,
				columns:
					visibleCount === 0
						? [emptyPeriodColumn(group.id)]
						: group.columns.map((column) =>
								buildMetricColumn(column, isWipColumnVisible, visibleCount),
							),
			})),
		[viewModel.periodGroups, isWipColumnVisible, visibleCount],
	);

	const rows = useMemo<DataGridRow<WipAnalysisGridRowData>[]>(() => {
		if (viewModel.periodGroups.length === 0) {
			return [];
		}

		const totalRow: DataGridRow<WipAnalysisGridRowData> = {
			id: TOTAL_ROW_ID,
			label: WIP_ANALYSIS_PORTFOLIO_TOTAL_LABEL,
			pinToTop: true,
			navigable: true,
			hideExpansionSpacer: true,
			renderLabel: () => <WipPortfolioTotalIdentityLabel />,
			data: { cells: viewModel.totalRow.cells },
		};

		const projectRows: DataGridRow<WipAnalysisGridRowData>[] = viewModel.projectRows.map(
			(projectRow) => ({
				id: projectRowId(projectRow.projectId),
				label: formatEmpty(projectRow.projectName),
				navigable: true,
				hideExpansionSpacer: true,
				renderLabel: () => <WipProjectIdentityLabel row={projectRow} />,
				data: { cells: projectRow.cells },
			}),
		);

		return [totalRow, ...projectRows];
	}, [viewModel.periodGroups, viewModel.projectRows, viewModel.totalRow.cells]);

	return (
		<DataGrid<WipAnalysisGridRowData>
			columns={columns}
			rows={rows}
			labelColumn={{
				label: "Project",
				width: WIP_IDENTITY_LABEL_WIDTH,
				hideExpansionSpacer: true,
				renderHeader: labelColumnHeader,
				renderSubHeader: <WipIdentityHeader />,
			}}
			overlay={gridOverlay}
			testId="wip-analysis-grid"
			emptyMessage={
				hasPeriods && selectedCount === 0
					? "No periods selected."
					: "No WIP analysis data available."
			}
			scrollPadding={WIP_ANALYSIS_GRID_SCROLL_PADDING}
			rowHeight={26}
		/>
	);
};
